import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { onAuthStateChanged, signInWithCustomToken, signOut, type User } from "firebase/auth";
import { auth, ENDUSER_API } from "./firebase";

interface SignInWithVipps {
  (): void;
  (options: { returnTo?: string }): void;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  flags: string[];
  signInWithVipps: SignInWithVipps;
  signOutUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [flags, setFlags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, async (next) => {
      setUser(next);
      if (next) {
        const token = await next.getIdTokenResult(true);
        setFlags((token.claims.flags as string[] | undefined) ?? []);
      } else {
        setFlags([]);
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("vipps_token");
    if (!token) return;
    url.searchParams.delete("vipps_token");
    window.history.replaceState({}, "", url.toString());
    void signInWithCustomToken(auth, token).catch((err) => {
      console.error("Vipps sign-in failed", err);
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        flags,
        signInWithVipps: ((options?: { returnTo?: string }) => {
          const returnTo = new URL(
            options?.returnTo ?? window.location.href,
            window.location.origin,
          );
          returnTo.searchParams.delete("vipps_token");
          window.location.href = `${ENDUSER_API}/vipps/login?return_to=${encodeURIComponent(returnTo.toString())}`;
        }) as SignInWithVipps,
        signOutUser: () => signOut(auth),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
